import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Edit, Eye, EyeOff, Key, Ban, ToggleLeft, ToggleRight, AlertCircle, Check, ArrowLeft, MapPin, UserPlus, Mail, Building, Building2, Store, Upload, Server, Settings, Bell, Send, MessageSquare, Smartphone, Shield, ShieldCheck, ShieldAlert, Database, Bot, UserCheck, Tags, Briefcase, Car } from 'lucide-react';
import MfaSetup from './MfaSetup';

import { api } from '../services/api';
import { authService } from '../services/authService';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { useAdminToast } from './admin/AdminToast';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';
import LeadCategoryManagement from './LeadCategoryManagement';
import FileUploadManagement from './FileUploadManagement';
import OfficeMappingManagement from './OfficeMappingManagement';
import OfficeMappingCategoryManagement from './OfficeMappingCategoryManagement';
import NodeServerManagement from './NodeServerManagement';
import AssistantManagement from './AssistantManagement';
import EnterpriseManagement from './EnterpriseManagement';
import BOCManagement from './BOCManagement';
import StoreManagement from './StoreManagement';
import SalesRepsManagement from './SalesRepsManagement';
import EmailConfigManagement from './EmailConfigManagement';
import HandoffConfigManagement from './HandoffConfigManagement';
import SmsButtonManagement from './SmsButtonManagement';
import ReceivedMessageConfigManagement from './ReceivedMessageConfigManagement';
import TelegramConfigsManagement from './TelegramConfigsManagement';
import SlackConfigsManagement from './SlackConfigsManagement';
import IntentManagement from './IntentManagement';
import DefaultIntentManagement from './DefaultIntentManagement';
import DefaultVehicleMappingManagement from './DefaultVehicleMappingManagement';
import VehicleNameMappingManagement from './VehicleNameMappingManagement';

const Admin = ({ user, defaultStore }) => {
  console.log('Admin component mounting with user:', user);
  
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(authService.isSuperAdmin() ? 'system' : 'outbound-leads');
  const [activeCategory, setActiveCategory] = useState(authService.isSuperAdmin() ? 'users' : 'categories');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { confirm, confirmDialog } = useConfirmDialog();
  const { showSuccess, toastElement } = useAdminToast();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'USER',
    enterpriseId: ''
  });
  const [createErrors, setCreateErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [updating, setUpdating] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [togglingUserId, setTogglingUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loadingMfaStatus, setLoadingMfaStatus] = useState(false);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [loadingFeatureFlags, setLoadingFeatureFlags] = useState(false);
  const [editingFeatureFlags, setEditingFeatureFlags] = useState(false);
  const [featureFlagsForm, setFeatureFlagsForm] = useState({});

  useEffect(() => {
    console.log('Admin useEffect triggered - calling loadUsers');
    if (authService.isSuperAdmin()) {
      loadUsers();
      loadEnterprises();
      loadFeatureFlags();
    } else {
      setLoading( false);
    }
    loadMfaStatus();
  }, []);

  const loadUsers = async () => {
    console.log('loadUsers function called');
    setError('');
    try {
      console.log('Making API call to /admin/users');
      const response = await api.get('/admin/users');
      console.log('API response:', response);
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Network error loading users');
    } finally {
      setLoading(false);
    }
  };

  const loadEnterprises = async () => {
    try {
      const response = await api.get('/admin/enterprises');
      setEnterprises(response.data);
    } catch (error) {
      console.error('Error loading enterprises:', error);
    }
  };

  const loadMfaStatus = async () => {
    setLoadingMfaStatus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api.defaults.baseURL}/auth/mfa/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setMfaStatus(data);
    } catch (error) {
      console.error('Error loading MFA status:', error);
    } finally {
      setLoadingMfaStatus(false);
    }
  };

  const loadFeatureFlags = async () => {
    setLoadingFeatureFlags(true);
    try {
      const response = await api.get('/admin/feature-flags');
      setFeatureFlags(response.data);
      setFeatureFlagsForm(response.data);
    } catch (error) {
      console.error('Error loading feature flags:', error);
    } finally {
      setLoadingFeatureFlags(false);
    }
  };

  const handleUpdateFeatureFlags = async () => {
    try {
      const response = await api.put('/admin/feature-flags', featureFlagsForm);
      setFeatureFlags(response.data);
      setEditingFeatureFlags(false);
      showSuccess('Feature flags updated successfully');
    } catch (error) {
      console.error('Error updating feature flags:', error);
      setError('Failed to update feature flags');
    }
  };

  const handleDisableMfa = async () => {
    const confirmed = await confirm({
      title: 'Disable two-factor authentication?',
      message: 'Your account will be less secure without two-factor authentication.',
      confirmLabel: 'Disable',
    });
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api.defaults.baseURL}/auth/mfa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        showSuccess('Two-factor authentication disabled');
        loadMfaStatus();
      }
    } catch (error) {
      console.error('Error disabling MFA:', error);
    }
  };

  const validateCreateForm = () => {
    const errors = {};
    const username = formData.username.trim();
    if (!username) {
      errors.username = 'Username is required';
    } else if (username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (formData.role === 'USER' && !formData.enterpriseId) {
      errors.enterpriseId = 'Enterprise is required for a user';
    }
    return errors;
  };

  const handleOpenCreateForm = () => {
    setEditingUser(null);
    setError('');
    setFormData({ username: '', password: '', role: 'USER', enterpriseId: '' });
    setCreateErrors({});
    setShowCreatePassword(false);
    setShowCreateForm(true);
  };

  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
    setError('');
    setCreateErrors({});
    setShowCreatePassword(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const errors = validateCreateForm();
    setCreateErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setCreating(true);
    setError('');
    try {
      await api.post('/admin/users', { ...formData, username: formData.username.trim() });
      await loadUsers();
      handleCloseCreateForm();
      setFormData({ username: '', password: '', role: 'USER', enterpriseId: '' });
      showSuccess('User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.response?.data?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleStartEdit = (targetUser) => {
    setShowCreateForm(false);
    setError('');
    setEditingUser({ ...targetUser });
    setEditErrors({});
    setShowChangePassword(false);
    setNewPassword('');
    setShowNewPassword(false);
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setError('');
    setEditErrors({});
    setShowChangePassword(false);
    setNewPassword('');
  };

  const validateEditForm = () => {
    const errors = {};
    const username = (editingUser?.username || '').trim();
    if (!username) {
      errors.username = 'Username is required';
    } else if (username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    if (showChangePassword) {
      if (!newPassword) {
        errors.password = 'New password is required';
      } else if (newPassword.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
    }
    if (editingUser?.role === 'USER' && !editingUser?.enterpriseIdFk) {
      errors.enterpriseId = 'Enterprise is required for a user';
    }
    return errors;
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    const errors = validateEditForm();
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setUpdating(true);
    setError('');
    try {
      await api.put(`/admin/users/${editingUser.id}`, {
        username: editingUser.username.trim(),
        role: editingUser.role,
        enabled: editingUser.enabled,
        enterpriseId: editingUser.role === 'USER' ? editingUser.enterpriseIdFk : null
      });
      if (showChangePassword && newPassword) {
        await api.post(`/admin/users/${editingUser.id}/change-password`, { newPassword });
      }
      await loadUsers();
      handleCancelEdit();
      showSuccess('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.response?.data?.message || 'Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find((u) => u.id === userId);
    const label = user?.username ? `"${user.username}"` : 'this user';
    const confirmed = await confirm({
      title: `Delete user ${label}?`,
      message: `The user ${label} will be permanently deleted and will no longer be able to sign in.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setDeletingUserId(userId);
    setError('');
    try {
      await api.delete(`/admin/users/${userId}`);
      await loadUsers();
      showSuccess('User deleted');
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleToggleStatus = async (userId) => {
    setTogglingUserId(userId);
    setError('');
    try {
      await api.post(`/admin/users/${userId}/toggle-status`);
      await loadUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      setError(error.response?.data?.message || 'Failed to toggle user status');
    } finally {
      setTogglingUserId(null);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-red-100 text-red-800';
      case 'ADMIN':
        return 'bg-purple-100 text-purple-800';
      case 'USER':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <AdminLoading message="Loading users..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg w-full min-h-[calc(100vh-4rem)]">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-gray-600 mr-3" />
                <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
              </div>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </button>
            </div>

            {/* Admin Tabs - Categorized */}
            <div className="mb-6">
              <div className="flex space-x-1 border-b border-gray-200">
                {authService.isSuperAdmin() && (
                  <button
                    onClick={() => { setActiveTab('system'); setActiveCategory('users'); }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'system'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    System
                  </button>
                )}
                <button
                  onClick={() => { setActiveTab('outbound-leads'); setActiveCategory('categories'); }}
                  className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                    activeTab === 'outbound-leads'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Outbound Leads
                </button>
                {authService.isSuperAdmin() && (
                  <button
                    onClick={() => { setActiveTab('organisation'); setActiveCategory('enterprises'); }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'organisation'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Building className="h-4 w-4 mr-2" />
                    Organisation
                  </button>
                )}
                {authService.isSuperAdmin() && (
                  <button
                    onClick={() => { setActiveTab('office'); setActiveCategory('nodes'); }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'office'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Office
                  </button>
                )}
                {authService.isAdmin() && (
                  <button
                    onClick={() => { setActiveTab('intents'); setActiveCategory('intents'); }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'intents'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Bot className="h-4 w-4 mr-2" />
                    Intents
                  </button>
                )}
                {authService.isAdmin() && (
                  <button
                    onClick={() => { setActiveTab('handoff'); setActiveCategory('sales-reps'); }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'handoff'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    Handoff & Escalation
                  </button>
                )}
                {authService.isAdmin() && (
                  <button
                    onClick={() => { setActiveTab('vehicle-data'); setActiveCategory('vehicle-name-mappings'); }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'vehicle-data'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Database className="h-4 w-4 mr-2" />
                    Vehicle Data
                  </button>
                )}
                {authService.isAdmin() && (
                  <button
                    onClick={() => { setActiveTab('notifications'); setActiveCategory('telegram'); }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'notifications'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Notifications
                  </button>
                )}
                <button
                  onClick={() => { setActiveTab('security'); setActiveCategory('mfa'); }}
                  className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                    activeTab === 'security'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Security
                </button>
              </div>

              {/* Sub-tabs for each category */}
              {activeTab === 'system' && authService.isSuperAdmin() && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setActiveCategory('users')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'users'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Users
                  </button>
                  <button
                    onClick={() => setActiveCategory('feature-flags')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'feature-flags'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Settings className="h-3 w-3 mr-1" />
                    Feature Flags
                  </button>
                </div>
              )}

              {activeTab === 'outbound-leads' && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setActiveCategory('categories')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'categories'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Tags className="h-3 w-3 mr-1" />
                    Lead Categories
                  </button>
                  <button
                    onClick={() => setActiveCategory('files')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'files'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    Bulk Lead Upload
                  </button>
                </div>
              )}

              {activeTab === 'organisation' && authService.isSuperAdmin() && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setActiveCategory('enterprises')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'enterprises'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Building2 className="h-3 w-3 mr-1" />
                    Enterprises
                  </button>
                  <button
                    onClick={() => setActiveCategory('bocs')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'bocs'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Briefcase className="h-3 w-3 mr-1" />
                    BOCs
                  </button>
                  <button
                    onClick={() => setActiveCategory('stores')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'stores'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Store className="h-3 w-3 mr-1" />
                    Stores
                  </button>
                  <button
                    onClick={() => setActiveCategory('assistants')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'assistants'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Bot className="h-3 w-3 mr-1" />
                    Assistants
                  </button>
                </div>
              )}

              {activeTab === 'office' && authService.isSuperAdmin() && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setActiveCategory('nodes')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'nodes'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Server className="h-3 w-3 mr-1" />
                    Server Nodes
                  </button>
                  <button
                    onClick={() => setActiveCategory('office-mapping')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'office-mapping'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Building className="h-3 w-3 mr-1" />
                    Office Mapping
                  </button>
                  <button
                    onClick={() => setActiveCategory('office-mapping-category')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'office-mapping-category'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Tags className="h-3 w-3 mr-1" />
                    Office Categories
                  </button>
                </div>
              )}

              {activeTab === 'intents' && authService.isAdmin() && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setActiveCategory('intents')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'intents'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <MessageSquare className="h-3 w-3 mr-1" />
                    Store Intents
                  </button>
                  <button
                    onClick={() => setActiveCategory('default-intents')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'default-intents'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Bot className="h-3 w-3 mr-1" />
                    Intent Templates
                  </button>
                </div>
              )}

              {activeTab === 'vehicle-data' && authService.isAdmin() && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setActiveCategory('vehicle-name-mappings')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'vehicle-name-mappings'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Car className="h-3 w-3 mr-1" />
                    Vehicle Name Mappings
                  </button>
                  <button
                    onClick={() => setActiveCategory('default-vehicle-data')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'default-vehicle-data'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Car className="h-3 w-3 mr-1" />
                    Default Vehicle Mappings
                  </button>
                </div>
              )}

              {activeTab === 'handoff' && authService.isAdmin() && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setActiveCategory('sales-reps')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'sales-reps'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Sales Reps
                  </button>
                  <button
                    onClick={() => setActiveCategory('email-config')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'email-config'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Mail className="h-3 w-3 mr-1" />
                    Handoff Email Server
                  </button>
                  <button
                    onClick={() => setActiveCategory('handoff-config')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'handoff-config'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <MessageSquare className="h-3 w-3 mr-1" />
                    Handoff Templates & SMS
                  </button>
                  <button
                    onClick={() => setActiveCategory('sms-buttons')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'sms-buttons'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Smartphone className="h-3 w-3 mr-1" />
                    Website SMS Widget
                  </button>
                </div>
              )}

              {activeTab === 'notifications' && authService.isAdmin() && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setActiveCategory('telegram')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'telegram'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Send className="h-3 w-3 mr-1" />
                    Telegram
                  </button>
                  <button
                    onClick={() => setActiveCategory('slack')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'slack'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <MessageSquare className="h-3 w-3 mr-1" />
                    Slack
                  </button>
                  <button
                    onClick={() => setActiveCategory('received-message')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'received-message'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Mail className="h-3 w-3 mr-1" />
                    New Message Alerts
                  </button>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setActiveCategory('mfa')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center ${
                      activeCategory === 'mfa'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Two-Factor Authentication
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-red-50 border border-red-100 p-4 mb-6">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Content */}
            {activeCategory === 'users' && authService.isSuperAdmin() && (
              <div className="space-y-6">
                <AdminPageHeader
                  icon={Users}
                  title="User Management"
                  subtitle="Manage admin and user accounts"
                  rightContent={<AdminAddButton onClick={handleOpenCreateForm} label="Add User" />}
                />

                {/* Create User Form */}
                {showCreateForm && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <UserPlus className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-medium text-gray-900">Create New User</h3>
                    </div>
                    <form onSubmit={handleCreateUser} noValidate className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                          <input
                            type="text"
                            value={formData.username}
                            onChange={(e) => {
                              setFormData({ ...formData, username: e.target.value });
                              if (createErrors.username) setCreateErrors({ ...createErrors, username: undefined });
                            }}
                            className={`block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 border focus:ring-blue-500 focus:border-blue-500 ${
                              createErrors.username ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder="jane.doe"
                          />
                          {createErrors.username && (
                            <p className="mt-1 text-xs text-red-600">{createErrors.username}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                          <div className="relative">
                            <input
                              type={showCreatePassword ? 'text' : 'password'}
                              value={formData.password}
                              onChange={(e) => {
                                setFormData({ ...formData, password: e.target.value });
                                if (createErrors.password) setCreateErrors({ ...createErrors, password: undefined });
                              }}
                              className={`block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 pr-9 border focus:ring-blue-500 focus:border-blue-500 ${
                                createErrors.password ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="At least 6 characters"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCreatePassword(!showCreatePassword)}
                              className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 hover:text-gray-600"
                              tabIndex={-1}
                            >
                              {showCreatePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {createErrors.password && (
                            <p className="mt-1 text-xs text-red-600">{createErrors.password}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                          <select
                            value={formData.role}
                            onChange={(e) => setFormData({
                              ...formData,
                              role: e.target.value,
                              enterpriseId: e.target.value === 'USER' ? formData.enterpriseId : ''
                            })}
                            className="block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="USER">User</option>
                            <option value="ADMIN">Admin</option>
                            {authService.isSuperAdmin() && <option value="SUPER_ADMIN">Super Admin</option>}
                          </select>
                        </div>
                      </div>
                      {formData.role === 'USER' && (
                        <div className="max-w-md">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Enterprise</label>
                          <select
                            value={formData.enterpriseId}
                            onChange={(e) => setFormData({ ...formData, enterpriseId: e.target.value ? Number(e.target.value) : '' })}
                            className={`block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 border focus:ring-blue-500 focus:border-blue-500 ${createErrors.enterpriseId ? 'border-red-300' : 'border-gray-300'}`}
                          >
                            <option value="">Select enterprise</option>
                            {enterprises.map((enterprise) => (
                              <option key={enterprise.id} value={enterprise.id}>{enterprise.name}</option>
                            ))}
                          </select>
                          {createErrors.enterpriseId && <p className="mt-1 text-xs text-red-600">{createErrors.enterpriseId}</p>}
                        </div>
                      )}
                      <div className="flex justify-end space-x-3 pt-2">
                        <button
                          type="button"
                          onClick={handleCloseCreateForm}
                          disabled={creating}
                          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={creating}
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {creating ? 'Creating…' : 'Create User'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Edit User Form */}
                {editingUser && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Edit className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-medium text-gray-900">Edit User — {editingUser.username}</h3>
                    </div>
                    <form onSubmit={handleUpdateUser} noValidate className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                          <input
                            type="text"
                            value={editingUser.username}
                            onChange={(e) => {
                              setEditingUser({ ...editingUser, username: e.target.value });
                              if (editErrors.username) setEditErrors({ ...editErrors, username: undefined });
                            }}
                            className={`block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 border focus:ring-blue-500 focus:border-blue-500 ${
                              editErrors.username ? 'border-red-300' : 'border-gray-300'
                            }`}
                          />
                          {editErrors.username && (
                            <p className="mt-1 text-xs text-red-600">{editErrors.username}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                          <select
                            value={editingUser.role}
                            onChange={(e) => setEditingUser({
                              ...editingUser,
                              role: e.target.value,
                              enterpriseIdFk: e.target.value === 'USER' ? editingUser.enterpriseIdFk : null
                            })}
                            className="block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="USER">User</option>
                            <option value="ADMIN">Admin</option>
                            {authService.isSuperAdmin() && <option value="SUPER_ADMIN">Super Admin</option>}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <button
                            type="button"
                            onClick={() => setEditingUser({ ...editingUser, enabled: !editingUser.enabled })}
                            className={`w-full flex items-center justify-between rounded-md shadow-sm sm:text-sm px-3 py-2 border transition-colors ${
                              editingUser.enabled
                                ? 'border-green-200 bg-green-50 text-green-700'
                                : 'border-gray-300 bg-white text-gray-500'
                            }`}
                          >
                            <span>{editingUser.enabled ? 'Enabled' : 'Disabled'}</span>
                            {editingUser.enabled ? (
                              <ToggleRight className="h-5 w-5 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      {editingUser.role === 'USER' && (
                        <div className="max-w-md">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Enterprise</label>
                          <select
                            value={editingUser.enterpriseIdFk || ''}
                            onChange={(e) => setEditingUser({ ...editingUser, enterpriseIdFk: e.target.value ? Number(e.target.value) : null })}
                            className={`block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 border focus:ring-blue-500 focus:border-blue-500 ${editErrors.enterpriseId ? 'border-red-300' : 'border-gray-300'}`}
                          >
                            <option value="">Select enterprise</option>
                            {enterprises.map((enterprise) => (
                              <option key={enterprise.id} value={enterprise.id}>{enterprise.name}</option>
                            ))}
                          </select>
                          {editErrors.enterpriseId && <p className="mt-1 text-xs text-red-600">{editErrors.enterpriseId}</p>}
                        </div>
                      )}

                      <div className="border-t border-gray-200 pt-4">
                        {!showChangePassword ? (
                          <button
                            type="button"
                            onClick={() => setShowChangePassword(true)}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
                          >
                            <Key className="h-4 w-4" />
                            Set a new password
                          </button>
                        ) : (
                          <div className="max-w-sm">
                            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                            <div className="relative">
                              <input
                                type={showNewPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => {
                                  setNewPassword(e.target.value);
                                  if (editErrors.password) setEditErrors({ ...editErrors, password: undefined });
                                }}
                                autoFocus
                                className={`block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 pr-9 border focus:ring-blue-500 focus:border-blue-500 ${
                                  editErrors.password ? 'border-red-300' : 'border-gray-300'
                                }`}
                                placeholder="At least 6 characters"
                              />
                              <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 hover:text-gray-600"
                                tabIndex={-1}
                              >
                                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                            {editErrors.password && (
                              <p className="mt-1 text-xs text-red-600">{editErrors.password}</p>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setShowChangePassword(false);
                                setNewPassword('');
                                setEditErrors({ ...editErrors, password: undefined });
                              }}
                              className="mt-1 text-xs text-gray-500 hover:text-gray-700"
                            >
                              Cancel password change
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end space-x-3 pt-2">
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          disabled={updating}
                          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={updating}
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updating ? 'Saving…' : 'Save Changes'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Users Table */}
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enterprise</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                            No users yet.
                          </td>
                        </tr>
                      )}
                      {users.map((tableUser) => (
                        <tr
                          key={tableUser.id}
                          onDoubleClick={() => handleStartEdit(tableUser)}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          title="Double-click to edit"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tableUser.username}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColor(tableUser.role)}`}>
                              {tableUser.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {tableUser.enterpriseIdFk
                              ? (enterprises.find((enterprise) => enterprise.id === tableUser.enterpriseIdFk)?.name || `#${tableUser.enterpriseIdFk}`)
                              : tableUser.role === 'USER'
                                ? <span className="text-red-600 font-medium">Not assigned</span>
                                : 'All'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              tableUser.enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              <span className={`w-2 h-2 rounded-full mr-2 ${
                                tableUser.enabled ? 'bg-green-500' : 'bg-red-500'
                              }`}></span>
                              {tableUser.enabled ? 'Active' : 'Disabled'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                            <AdminRowActions>
                              <AdminActionButton variant="edit" onClick={() => handleStartEdit(tableUser)} />
                              <AdminActionButton
                                variant={tableUser.enabled ? 'deactivate' : 'activate'}
                                loading={togglingUserId === tableUser.id}
                                loadingLabel="Updating…"
                                onClick={() => handleToggleStatus(tableUser.id)}
                              />
                              <AdminActionButton
                                variant="delete"
                                loading={deletingUserId === tableUser.id}
                                loadingLabel="Deleting…"
                                onClick={() => handleDeleteUser(tableUser.id)}
                              />
                            </AdminRowActions>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeCategory === 'feature-flags' && authService.isSuperAdmin() && (
              <div className="space-y-6">
                <AdminPageHeader
                  icon={Settings}
                  title="Feature Flags"
                  subtitle="Enable or disable system features"
                  rightContent={
                    !editingFeatureFlags && (
                      <button
                        onClick={() => setEditingFeatureFlags(true)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                    )
                  }
                />

                {loadingFeatureFlags ? (
                  <div className="text-center py-8 text-gray-500">Loading feature flags...</div>
                ) : featureFlags ? (
                  <div className="bg-white shadow rounded-lg p-6">
                    {editingFeatureFlags ? (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">Log Mode</h3>
                            <p className="text-xs text-gray-500 mt-1">Delay sending of low-confidence AI messages</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={featureFlagsForm.logModeEnabled || false}
                              onChange={(e) => setFeatureFlagsForm({ ...featureFlagsForm, logModeEnabled: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>

                        {featureFlagsForm.logModeEnabled && (
                          <div className="ml-4 space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Threshold (%)</label>
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                max="100"
                                value={featureFlagsForm.logModeConfidenceThreshold || 95}
                                onChange={(e) => setFeatureFlagsForm({ ...featureFlagsForm, logModeConfidenceThreshold: parseFloat(e.target.value) })}
                                className="block w-32 rounded-md shadow-sm sm:text-sm px-3 py-2 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Auto Send Delay (seconds)</label>
                              <input
                                type="number"
                                min="1"
                                value={featureFlagsForm.logModeAutoSendDelaySeconds || 30}
                                onChange={(e) => setFeatureFlagsForm({ ...featureFlagsForm, logModeAutoSendDelaySeconds: parseInt(e.target.value) })}
                                className="block w-32 rounded-md shadow-sm sm:text-sm px-3 py-2 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">Message Accumulation</h3>
                            <p className="text-xs text-gray-500 mt-1">Combine rapid messages before processing</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={featureFlagsForm.messageAccumulationEnabled || false}
                              onChange={(e) => setFeatureFlagsForm({ ...featureFlagsForm, messageAccumulationEnabled: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>

                        {featureFlagsForm.messageAccumulationEnabled && (
                          <div className="ml-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Accumulation Duration (seconds)</label>
                            <input
                              type="number"
                              min="1"
                              value={featureFlagsForm.messageAccumulationDurationSeconds || 15}
                              onChange={(e) => setFeatureFlagsForm({ ...featureFlagsForm, messageAccumulationDurationSeconds: parseInt(e.target.value) })}
                              className="block w-32 rounded-md shadow-sm sm:text-sm px-3 py-2 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        )}

                        <div className="flex space-x-3 pt-4 border-t">
                          <button
                            onClick={handleUpdateFeatureFlags}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Save Changes
                          </button>
                          <button
                            onClick={() => {
                              setEditingFeatureFlags(false);
                              setFeatureFlagsForm(featureFlags);
                            }}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">Log Mode</h3>
                            <p className="text-xs text-gray-500 mt-1">Delay sending of low-confidence AI messages</p>
                          </div>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            featureFlags.logModeEnabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {featureFlags.logModeEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>

                        {featureFlags.logModeEnabled && (
                          <div className="ml-4 text-sm text-gray-600">
                            <p>Confidence Threshold: {featureFlags.logModeConfidenceThreshold}%</p>
                            <p>Auto Send Delay: {featureFlags.logModeAutoSendDelaySeconds}s</p>
                          </div>
                        )}

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">Message Accumulation</h3>
                            <p className="text-xs text-gray-500 mt-1">Combine rapid messages before processing</p>
                          </div>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            featureFlags.messageAccumulationEnabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {featureFlags.messageAccumulationEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>

                        {featureFlags.messageAccumulationEnabled && (
                          <div className="ml-4 text-sm text-gray-600">
                            <p>Accumulation Duration: {featureFlags.messageAccumulationDurationSeconds}s</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">No feature flags available</div>
                )}
              </div>
            )}

            {activeCategory === 'categories' && (
              <LeadCategoryManagement />
            )}

            {activeCategory === 'intents' && authService.isAdmin() && (
              <>
                <div className="mb-4 flex items-center text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                  Bootstrapping a new store? Copy a starting set from{' '}
                  <button
                    type="button"
                    onClick={() => setActiveCategory('default-intents')}
                    className="ml-1 font-medium text-blue-600 hover:text-blue-700 underline"
                  >
                    Intent Templates
                  </button>
                </div>
                <IntentManagement />
              </>
            )}

            {activeCategory === 'default-intents' && authService.isAdmin() && (
              <DefaultIntentManagement defaultStore={defaultStore} />
            )}

            {activeCategory === 'default-vehicle-data' && authService.isAdmin() && (
              <DefaultVehicleMappingManagement />
            )}

            {activeCategory === 'vehicle-name-mappings' && authService.isAdmin() && (
              <VehicleNameMappingManagement />
            )}

            {activeCategory === 'files' && (
              <FileUploadManagement />
            )}

            {activeCategory === 'sales-reps' && authService.isAdmin() && (
              <SalesRepsManagement />
            )}

            {activeCategory === 'email-config' && authService.isAdmin() && (
              <EmailConfigManagement />
            )}

            {activeCategory === 'handoff-config' && authService.isAdmin() && (
              <HandoffConfigManagement />
            )}

            {activeCategory === 'sms-buttons' && (
              <SmsButtonManagement />
            )}

            {activeCategory === 'telegram' && authService.isAdmin() && (
              <TelegramConfigsManagement />
            )}

            {activeCategory === 'slack' && authService.isAdmin() && (
              <SlackConfigsManagement />
            )}

            {activeCategory === 'received-message' && authService.isAdmin() && (
              <ReceivedMessageConfigManagement />
            )}

            {activeCategory === 'mfa' && (
              <div className="space-y-6">
                <AdminPageHeader
                  icon={Shield}
                  title="Two-Factor Authentication"
                  subtitle="Secure your account with TOTP-based authentication"
                />

                {showMfaSetup ? (
                  <MfaSetup
                    onCancel={() => {
                      setShowMfaSetup(false);
                      loadMfaStatus();
                    }}
                    onSuccess={() => {
                      setShowMfaSetup(false);
                      loadMfaStatus();
                      showSuccess('Two-factor authentication enabled successfully');
                    }}
                  />
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    {loadingMfaStatus ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center">
                            {mfaStatus?.enabled ? (
                              <div className="h-12 w-12 flex items-center justify-center rounded-full bg-green-100 mr-4">
                                <ShieldCheck className="h-6 w-6 text-green-600" />
                              </div>
                            ) : (
                              <div className="h-12 w-12 flex items-center justify-center rounded-full bg-yellow-100 mr-4">
                                <ShieldAlert className="h-6 w-6 text-yellow-600" />
                              </div>
                            )}
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">
                                {mfaStatus?.enabled ? 'MFA Enabled' : 'MFA Disabled'}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {mfaStatus?.status || 'Two-factor authentication is not configured'}
                              </p>
                            </div>
                          </div>
                          {mfaStatus?.enabled ? (
                            <button
                              onClick={handleDisableMfa}
                              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                            >
                              <Ban className="h-4 w-4 mr-2" />
                              Disable MFA
                            </button>
                          ) : (
                            <button
                              onClick={() => setShowMfaSetup(true)}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                              <Smartphone className="h-4 w-4 mr-2" />
                              Enable MFA
                            </button>
                          )}
                        </div>

                        {mfaStatus?.enabled && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center">
                              <ShieldCheck className="h-4 w-4 mr-2" />
                              Your account is protected
                            </h4>
                            <p className="text-sm text-green-700">
                              Two-factor authentication is active. You'll need to enter a code from your authenticator app when logging in.
                            </p>
                            {mfaStatus.backupCodesRemaining > 0 && (
                              <p className="text-sm text-green-700 mt-2">
                                You have {mfaStatus.backupCodesRemaining} backup codes remaining.
                              </p>
                            )}
                          </div>
                        )}

                        {!mfaStatus?.enabled && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                              <Smartphone className="h-4 w-4 mr-2" />
                              Recommended: Enable Two-Factor Authentication
                            </h4>
                            <p className="text-sm text-blue-700">
                              Add an extra layer of security to your account by enabling two-factor authentication. You'll use an authenticator app (like Google Authenticator or Authy) to generate codes.
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeCategory === 'nodes' && authService.isSuperAdmin() && (
              <NodeServerManagement />
            )}

            {activeCategory === 'office-mapping' && authService.isSuperAdmin() && (
              <OfficeMappingManagement />
            )}

            {activeCategory === 'office-mapping-category' && authService.isSuperAdmin() && (
              <OfficeMappingCategoryManagement />
            )}

            {activeCategory === 'enterprises' && authService.isSuperAdmin() && (
              <EnterpriseManagement />
            )}

            {activeCategory === 'bocs' && authService.isSuperAdmin() && (
              <BOCManagement />
            )}

            {activeCategory === 'stores' && authService.isSuperAdmin() && (
              <StoreManagement />
            )}

            {activeCategory === 'assistants' && authService.isSuperAdmin() && (
              <AssistantManagement />
            )}
          </div>
        </div>
      </div>
      {confirmDialog}
      {toastElement}      </div>
  );
};

export default Admin;
